import { CircularProgress } from '@mui/material'
import clsx from 'clsx'
import { Button } from 'components/buttons'
import { Input } from 'components/input'
import { Typography } from 'components/typography'
import { Icons, TagBoxProps, TypographyTypes } from 'interfaces'
import React, { useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { PRIMARY_BLUE_COLOR } from 'utils'

export const TagBox: React.FC<TagBoxProps> = ({
	resetInput,
	handleChangeDebounce,
	tagTitle,
	setSearchTags,
	searchTags,
	dataTagBox,
	isLoadingTagBox,
	onClose,
	fetchNextPageReach,
	hasNextPageReach,
	isFetchingNextPageReach
}) => {
	const { ref, inView } = useInView()

	useEffect(() => {
		if (
			inView &&
			hasNextPageReach &&
			!isFetchingNextPageReach &&
			!isLoadingTagBox
		) {
			fetchNextPageReach?.()
		}
	}, [inView, isFetchingNextPageReach])

	const data = useMemo(
		() => dataTagBox?.pages.flatMap(pageReach => pageReach?.items),
		[dataTagBox]
	)

	const handleClose = (): void => {
		setSearchTags('')
		onClose?.()
	}

	return (
		<div className="w-full flex flex-col items-center gap-4 py-4 h-full">
			<div className="border-2 rounded-full border-blue-primary px-4 py-2 w-full">
				<Typography
					title={tagTitle || ''}
					type={TypographyTypes.h2}
					fontLeading="15/18"
					className="text-[#5A5A5A] !font-normal text-center"
				/>
			</div>
			<div className="w-full flex items-center justify-center">
				<Input
					rightClick={() => {
						resetInput?.()
						setSearchTags('')
					}}
					name="search"
					onChangeCustom={e => {
						setSearchTags(e.target.value)
						handleChangeDebounce?.(e)
					}}
					search
					className="w-full"
					placeholder="Search users"
					rightIcon={Icons.cancel}
					inputClassName="!p-[unset] !py-1 !px-2 !rounded-[16px]"
					inputFocus
					borderFull={false}
					value={searchTags}
				/>
			</div>

			<div className="w-full flex items-center justify-between pr-4">
				<Typography
					title="Name"
					type={TypographyTypes.h2}
					fontLeading="15/18"
					className="text-[#5A5A5A] !font-bold text-center"
				/>
				<Typography
					title="Email"
					type={TypographyTypes.h2}
					fontLeading="15/18"
					className="text-[#5A5A5A] !font-bold text-center"
				/>
			</div>

			{isLoadingTagBox && !data?.length && (
				<div className="w-full flex flex-col items-center justify-center h-[30vh]">
					<CircularProgress disableShrink />
				</div>
			)}

			{!isLoadingTagBox && !data?.length && (
				<div className="w-full flex items-center justify-center h-[30vh]">
					<Typography
						title="No users found"
						type={TypographyTypes.h3}
						fontLeading="18/21"
						className="text-gray-1"
					/>
				</div>
			)}

			<div className="w-full overflow-y-scroll scrollbar-simple scrollbar-stable h-[36vh]">
				{!isLoadingTagBox &&
					data?.map((user, index) => (
						<div
							key={index}
							className="grid grid-cols-2 w-full overflow-x-hidden h-12"
						>
							<Typography
								title={user?.name || ''}
								type={TypographyTypes.h2}
								fontLeading="15/18"
								className="text-[#5A5A5A] !font-normal "
							/>
							<Typography
								title={user?.email || ''}
								type={TypographyTypes.h2}
								fontLeading="15/18"
								className="text-[#5A5A5A] !font-normal truncate w-[200px]"
							/>
						</div>
					))}

				<div
					ref={ref}
					className={clsx('w-full flex items-center justify-center')}
				>
					{isFetchingNextPageReach && (
						<CircularProgress
							style={{ color: PRIMARY_BLUE_COLOR }}
							disableShrink
							size={30}
						/>
					)}
				</div>
			</div>
			<div className="absolute bottom-0 right-0 pr-4">
				<Button
					type="button"
					label="Back"
					color="Transparent"
					className="!min-w-[130px]"
					onClick={handleClose}
				/>
			</div>
		</div>
	)
}
